import { useEffect, useMemo, useState } from "react";
import { useCreateBlockNote } from "@blocknote/react";
import { BlockNoteView } from "@blocknote/mantine";
import { BlockNoteSchema, defaultBlockSpecs, type PartialBlock } from "@blocknote/core";

export type NoteEditorProps = {
  initialContent?: PartialBlock[];
  onChange: (blocks: PartialBlock[]) => void;
  defaultInitialContent?: PartialBlock[];
  autoFocus?: boolean;
  editable?: boolean;
};

const _defaultInitialContent: PartialBlock[] = [
  {
    type: "paragraph",
  },
];

function schemaWithoutCodeBlock() {
  const { codeBlock: _omit, ...blockSpecs } = defaultBlockSpecs;
  return BlockNoteSchema.create({ blockSpecs });
}

// BlockNote requires inline content `text` to be a string. Sanitize blocks
// from backend data that may have null/undefined/non-string text values.
function sanitizeBlocks(blocks: PartialBlock[]): PartialBlock[] {
  const sanitized = blocks.map((block) => {
    if ((block as { type?: string }).type === "codeBlock") {
      return { type: "paragraph", content: "" } as PartialBlock;
    }
    const next = { ...block } as any;
    if (Array.isArray(next.content)) {
      next.content = next.content.map((item: any) => {
        if (item && typeof item === "object" && "text" in item) {
          return {
            ...item,
            text: String(item.text ?? ""),
          };
        }
        return item;
      });
    }
    return next;
  });
  return sanitized as PartialBlock[];
}

export default function NoteEditor({
  initialContent,
  onChange,
  defaultInitialContent = _defaultInitialContent,
  autoFocus = true,
  editable = true,
}: NoteEditorProps) {
  const schema = useMemo(() => schemaWithoutCodeBlock(), []);

  const editorInitialContent = useMemo(
    () => (initialContent?.length ? sanitizeBlocks(initialContent) : defaultInitialContent),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only compute on mount;
    // the parent uses a React key (e.g. key={noteId}) to force remount when switching notes
    []
  );

  const editor = useCreateBlockNote(
    {
      schema,
      initialContent: editorInitialContent,
    },
    [schema],
  );

  // Track dark mode so BlockNote renders with a readable palette. Hardcoding
  // `theme="light"` inside a dark sheet leaves near-black text on our dark
  // card background, which is what the user was seeing in meeting summaries,
  // notes, task notes, and follow-up email drafts.
  const [isDark, setIsDark] = useState<boolean>(() =>
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark")
  );
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const update = () => setIsDark(root.classList.contains("dark"));
    update();
    const observer = new MutationObserver(update);
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (autoFocus && editor) {
      // Use a short timeout to let BlockNote's ProseMirror view and slash-menu
      // plugin fully initialize. requestAnimationFrame alone is not enough when
      // the editor lives inside a Radix Dialog/Sheet (focus trap interferes).
      const timer = setTimeout(() => {
        editor.focus();
        const doc = editor.document;
        const firstBlock = doc[0];
        if (firstBlock) {
          editor.setTextCursorPosition(firstBlock.id, "end");
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [editor, autoFocus]);

  return (
    <BlockNoteView
      editor={editor}
      theme={isDark ? "dark" : "light"}
      onChange={() => onChange(editor.document)}
      sideMenu={false}
      comments={false}
      editable={editable}
    />
  );
}
